import type { MediaRef, SeoMeta, TranslatableString } from '@geoic/shared';
import { Input, Checkbox } from '@/components/ui/Field.js';
import { TranslatableInput } from '@/components/form/TranslatableInput.js';
import { MediaPicker } from '@/components/media/MediaPicker.js';
import { Card, CardBody, CardHeader } from '@/components/ui/Card.js';

export type SeoPanelProps = {
  value: SeoMeta;
  onChange: (value: SeoMeta) => void;
  /** Bo'sh qoldirilganda nima ishlatilishini aytish uchun. */
  fallbackTitle?: string;
};

/**
 * SEO paneli — sahifa, yangilik, xizmat, loyiha va kursda bir xil.
 *
 * Maydonlar BO'SH qolishi normal: `generateMetadata` (B11) bo'sh bo'lsa
 * sarlavha va matndan o'zi yasaydi. Shuning uchun bu yerda hech narsa
 * majburiy emas — muharrirni har safar SEO to'ldirishga majburlash
 * kontent qo'shishni sekinlashtirardi.
 */
export function SeoPanel({ value: raw, onChange, fallbackTitle }: SeoPanelProps) {
  // Kelgan qiymat TO'LIQ bo'lmasligi mumkin: qoralamada `seo` faqat bitta
  // maydon bilan saqlangan bo'lishi mumkin, eski yozuvlarda esa u umuman
  // yo'q. To'ldirilmasa `metaTitle.uz` o'qishda butun ekran oq qolardi.
  const value = withSeoDefaults(raw);

  function set<K extends keyof SeoMeta>(key: K, next: SeoMeta[K]): void {
    onChange({ ...value, [key]: next });
  }

  return (
    <Card>
      <CardHeader
        title="SEO"
        description="Bo‘sh qoldirilsa sarlavha va matndan avtomatik yasaladi"
      />
      <CardBody className="space-y-4">
        <TranslatableInput
          label="Meta sarlavha"
          value={value.metaTitle as TranslatableString}
          onChange={(next) => set('metaTitle', next)}
          hint={
            fallbackTitle
              ? `Bo‘sh qoldirilsa: «${fallbackTitle}»`
              : 'Bo‘sh qoldirilsa sahifa sarlavhasi ishlatiladi'
          }
        />

        <TranslatableInput
          label="Meta tavsif"
          multiline
          rows={3}
          value={value.metaDescription as TranslatableString}
          onChange={(next) => set('metaDescription', next)}
          hint="Qidiruv natijasida chiqadigan matn — 150–160 belgi tavsiya etiladi"
        />

        <MediaPicker
          label="Ijtimoiy tarmoq rasmi (OG)"
          value={value.ogImage as MediaRef | null}
          onChange={(next) => set('ogImage', next)}
          hint="Havola Telegram yoki Facebook'da ulashilganda ko‘rinadi. 1200×630"
        />

        <Input
          label="Kanonik manzil"
          value={value.canonicalUrl ?? ''}
          placeholder="https://geoic.uz/…"
          onChange={(event) => set('canonicalUrl', event.target.value || null)}
          hint="Bir xil kontent bir nechta manzilda bo‘lsa to‘ldiriladi. Odatda bo‘sh"
        />

        <Checkbox
          label="Qidiruv tizimlaridan yashirish (noindex)"
          hint="Sahifa saytda ochiladi, lekin Google natijalariga tushmaydi"
          checked={value.noIndex === true}
          onChange={(event) => set('noIndex', event.target.checked)}
        />
      </CardBody>
    </Card>
  );
}

/** Bo'sh SEO — yangi forma uchun. */
export function emptySeo(): SeoMeta {
  return {
    metaTitle: { uz: '', ru: '', en: '' },
    metaDescription: { uz: '', ru: '', en: '' },
    ogImage: null,
    canonicalUrl: null,
    noIndex: false,
  };
}

/** Yetishmayotgan maydonlarni to'ldiradi — qisman kelgan `seo` uchun. */
export function withSeoDefaults(value: SeoMeta | null | undefined): SeoMeta {
  return { ...emptySeo(), ...(value ?? {}) };
}
